import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Paragraph } from '@treams/treams-ui';

function receiveCode(params: URLSearchParams, workspaceID: string, stateKey: string) {
	if (!params.has('state') || !params.has('code')) {
		return;
	}

	const desiredState = localStorage.getItem(stateKey);
	if (
		params.get('state') !== desiredState &&
		params.get('state') !== `${workspaceID}|${desiredState}`
	) {
		return;
	}

	return params.get('code');
}

function createOAuth2State(workspaceID: string, stateKey: string) {
	const randomString = Math.random().toString().substring(0, 10);
	localStorage.setItem(stateKey, randomString);
	return encodeURIComponent(`${workspaceID}|${randomString}`);
}

export default function ConfigurationFieldOAuth2(props: {
	field: graphql.ConnectorConfigFieldOAuth2;
	autoFocus?: boolean;
	change?: Partial<graphql.UpdateConnectorConfigFieldInput>;
	onChange: (input: Partial<graphql.UpdateConnectorConfigFieldInput>) => Promise<void>;
}) {
	const history = useHistory();
	const params = new URLSearchParams(history.location.search);
	const { pathname } = useLocation();
	const { t } = useTranslation();
	const { id: workspaceID } = useWorkspace();

	const authorizeUrl = props.field.authorizeUrl;
	const stateKey = 'IntegrationsOAuth2State';

	const code = receiveCode(params, workspaceID, stateKey);

	useEffect(() => {
		if (!code || code.length === 0) {
			return;
		}

		(async () => {
			await props.onChange({ textValue: code });
			history.push(pathname);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [code]);

	const { authorizationInfo } = props.field;

	return (
		<>
			<Box mr={2} flexGrow={1}>
				{authorizationInfo ? (
					<Paragraph>
						{t('connectors.settings.configuration.oauth2.configuredAs')}{' '}
						<strong>{authorizationInfo}</strong>
					</Paragraph>
				) : undefined}
			</Box>
			<Button
				small
				title={
					props.field.authorizationInfo
						? t('connectors.settings.configuration.oauth2.changeLogin')
						: t('connectors.settings.configuration.oauth2.initialLogin')
				}
				type={props.field.authorizationInfo ? 'secondary' : 'primary'}
				onClick={() => {
					const state = createOAuth2State(workspaceID, stateKey);
					window.location.href = `${authorizeUrl}&state=${state}`;
				}}
			/>
		</>
	);
}
